import React, { useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { FileSpreadsheet, Download, Upload, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Field, inp, VisibilityBadge } from "@/components/admin";

export function ExcelImportDialog({ courses, batches, onDone }) {
  const [open, setOpen] = useState(false);
  const [courseId, setCourseId] = useState("");
  const [batchId, setBatchId] = useState("");
  const [autoZoom, setAutoZoom] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const reset = () => { setPreview(null); setCourseId(""); setBatchId(""); setAutoZoom(false); if (fileRef.current) fileRef.current.value = ""; };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/admin/classes/excel-template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "cwk_class_schedule_template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download template"); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!courseId) { toast.error("Pehle course select karo"); e.target.value = ""; return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("course_id", courseId);
      if (batchId) fd.append("batch_id", batchId);
      const { data } = await api.post("/admin/classes/import-excel/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (er) {
      toast.error(er.response?.data?.detail || "Failed to parse Excel");
    } finally { setBusy(false); }
  };

  const commit = async () => {
    if (!preview?.rows?.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/classes/import-excel/commit", {
        course_id: courseId, batch_id: batchId || null,
        rows: preview.rows, auto_create_zoom: autoZoom,
      });
      toast.success(`✅ Imported ${data.created} classes (skipped ${data.skipped} duplicates)`);
      onDone?.(); reset(); setOpen(false);
    } catch (er) { toast.error(er.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  const courseBatches = batches.filter((b) => b.course_id === courseId);

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <button className="btn-ghost text-sm" data-testid="open-excel-import">
          <FileSpreadsheet className="w-4 h-4" /> Import from Excel
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--bg-elev-2)] border-white/10 max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-fuchsia-300" /> Bulk import classes from Excel</DialogTitle>
          <div className="text-xs text-[var(--text-mute)]">Upload .xlsx with class plan. Preview karke selectively import karo. Visibility (draft/published/scheduled) Excel se pick hogi.</div>
        </DialogHeader>

        <div className="space-y-4">
          {/* Step 1: download template */}
          <div className="rounded-xl border border-fuchsia-400/20 bg-fuchsia-500/[0.04] p-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-fuchsia-500/15 flex items-center justify-center shrink-0"><Download className="w-4 h-4 text-fuchsia-300" /></div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider text-fuchsia-300 font-bold">Step 1 — Download template</div>
              <div className="text-[11px] text-white/50">Pre-filled sample with all column headers + instructions tab.</div>
            </div>
            <button type="button" onClick={downloadTemplate} className="btn-ghost text-xs" data-testid="excel-download-template">
              <Download className="w-3.5 h-3.5" /> Download .xlsx
            </button>
          </div>

          {/* Step 2: pick course + batch */}
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Course (required)">
              <select value={courseId} onChange={(e) => { setCourseId(e.target.value); setBatchId(""); setPreview(null); }} className={inp} data-testid="excel-course-select">
                <option value="">Select course…</option>
                {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </Field>
            <Field label="Batch (optional — leave empty to apply to course-wide)">
              <select value={batchId} onChange={(e) => setBatchId(e.target.value)} className={inp} disabled={!courseId} data-testid="excel-batch-select">
                <option value="">— No specific batch —</option>
                {courseBatches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </Field>
          </div>

          {/* Step 3: upload */}
          <div>
            <div className="text-xs text-[var(--text-dim)] mb-1.5">Step 2 — Upload filled .xlsx</div>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onFile} disabled={!courseId || busy} className={`${inp} file:mr-3 file:rounded-md file:border-0 file:bg-fuchsia-500/20 file:text-fuchsia-200 file:px-3 file:py-1.5 file:text-xs file:font-semibold`} data-testid="excel-file-input" />
            {!courseId && <div className="text-[11px] text-amber-300 mt-1">⚠️ Pehle course select karo</div>}
          </div>

          {/* Preview */}
          {preview && (
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-3" data-testid="excel-preview">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="text-sm font-semibold">Preview — {preview.total} rows</div>
                <div className="text-xs text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Will import: <b>{preview.will_import}</b></div>
                {preview.will_skip > 0 && <div className="text-xs text-amber-300 inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Skipping {preview.will_skip} duplicates</div>}
              </div>

              {preview.errors?.length > 0 && (
                <div className="rounded-lg bg-red-500/10 border border-red-400/20 p-2 text-xs text-red-300">
                  {preview.errors.map((e, i) => <div key={i}>• {e}</div>)}
                </div>
              )}

              <div className="overflow-x-auto max-h-[40vh]">
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-wider text-white/50 bg-white/5 sticky top-0">
                    <tr><th className="px-2 py-1.5 text-left">#</th><th className="px-2 py-1.5 text-left">Title</th><th className="px-2 py-1.5 text-left">When</th><th className="px-2 py-1.5 text-left">Min</th><th className="px-2 py-1.5 text-left">Teacher</th><th className="px-2 py-1.5 text-left">Mode</th><th className="px-2 py-1.5 text-left">Visibility</th><th className="px-2 py-1.5 text-left">Status</th></tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r, i) => (
                      <tr key={i} className={`border-t border-white/5 ${r.will_skip_duplicate ? "opacity-50" : ""}`}>
                        <td className="px-2 py-1.5">{r.day_number}</td>
                        <td className="px-2 py-1.5 max-w-xs truncate">{r.title}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{new Date(r.scheduled_at).toLocaleString()}</td>
                        <td className="px-2 py-1.5">{r.duration_minutes}</td>
                        <td className="px-2 py-1.5 text-[10px]">{r.teacher_email || "—"}</td>
                        <td className="px-2 py-1.5"><span className={`px-1.5 py-0.5 rounded uppercase text-[9px] ${r.mode === "online" ? "bg-pink-500/15 text-pink-200" : r.mode === "offline" ? "bg-amber-500/15 text-amber-200" : "bg-purple-500/15 text-purple-200"}`}>{r.mode}</span></td>
                        <td className="px-2 py-1.5"><VisibilityBadge visibility={r.visibility} /></td>
                        <td className="px-2 py-1.5 text-[10px]">{r.will_skip_duplicate ? <span className="text-amber-300">SKIP (dup)</span> : <span className="text-emerald-300">IMPORT</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <label className="inline-flex items-center gap-2 text-xs text-white/70">
                <input type="checkbox" checked={autoZoom} onChange={(e) => setAutoZoom(e.target.checked)} data-testid="excel-auto-zoom" />
                Auto-create Zoom meetings for non-draft rows (slower import)
              </label>

              <div className="flex gap-2 pt-1">
                <button type="button" onClick={reset} className="btn-ghost flex-1 justify-center text-sm">Cancel</button>
                <button disabled={busy || preview.will_import === 0} onClick={commit} className="btn-primary flex-1 justify-center text-sm disabled:opacity-60" data-testid="excel-commit-import">
                  <Upload className="w-4 h-4" /> {busy ? "Importing…" : `Import ${preview.will_import} classes`}
                </button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ExcelImportDialog;
